import { useState, useEffect, useCallback } from "react";
import {
  Button,
  Input,
  Upload,
  message,
  Row,
  Col,
  Typography,
  Spin,
  Tooltip,
  Modal
} from "antd";
import TextArea from "antd/es/input/TextArea";
import {
  UploadOutlined,
  InfoCircleOutlined,
  FileWordOutlined,
  SaveOutlined,
  DownloadOutlined,
  DeleteOutlined
} from "@ant-design/icons";
import { getUserID } from "../../Utility/service";
import "./style.scss";
import { axiosSecureInstance } from "../../api/axios";
import { createKnowledgeBase } from "../../api/knowledgeBase";
import NewLogo from "../../assests/images/collab_ai_logo_white.svg";
import { useNavigate, useLocation, useBeforeUnload } from "react-router-dom";

const { Text } = Typography;
const { confirm } = Modal;

const CompanyInfo = () => {
  const userId = getUserID();
  const [companyInfo, setCompanyInfo] = useState({
    name: "",
    website: "",
    description: "",
    files: null,
  });
  const [charCount, setCharCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [existingData, setExistingData] = useState(null);
  const [isGeneratingDoc, setIsGeneratingDoc] = useState(false);
  const [docPreview, setDocPreview] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [totalFileList, setTotalFileList] = useState([]);
  const [countTotalFile, setCountTotalFile] = useState(0);
  const [deletedFileIds, setDeletedFileIds] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Block navigation when there are unsaved changes
  useBeforeUnload(
    useCallback(
      (event) => {
        if (hasUnsavedChanges) {
          event.preventDefault();
          event.returnValue = '';
        }
      },
      [hasUnsavedChanges]
    )
  );

  // Prevent browser back/forward
  useEffect(() => {
    const handlePopState = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.stopPropagation();
        window.history.pushState(null, '', location.pathname);
        showUnsavedChangesModal();
      }
    };

    // Add multiple history states to prevent back navigation
    window.history.pushState(null, '', location.pathname);
    window.history.pushState(null, '', location.pathname);
    window.history.pushState(null, '', location.pathname);

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, location.pathname]);

  // Prevent keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const showUnsavedChangesModal = () => {
    confirm({
      title: 'Unsaved Changes',
      content: 'You have unsaved changes. Do you want to save them before leaving?',
      okText: 'Save and Leave',
      cancelText: 'Stay',
      onOk: async () => {
        try {
          await handleSubmit();
          navigate(-1);
        } catch (error) {
          window.history.pushState(null, '', location.pathname);
        }
      },
      onCancel: () => {
        window.history.pushState(null, '', location.pathname);
      },
    });
  };

  const fetchCompanyInfo = async () => {
    try {
      const response = await axiosSecureInstance.get(`/api/company-submission/${userId}`);
      if (response.status === 200 && response.data) {
        const data = response.data;
        setCompanyInfo({
          name: data.companyName || "",
          website: data.websiteUrl || "",
          description: data.companyInformation || "",
          files: data.fileInfo?.filter((file) => file.generatedDoc !== true) || null,
        });
        if(data.companyName ==="" || data.websiteUrl ==="" || data.companyInformation ==="") {
          setIsSaved(false);
        } else {
          setIsSaved(true);
          setExistingData(data);
        }   

        if (data.generatedDocPath) {
          const fileName = data.generatedDocPath.split('/').pop();
          setDocPreview({
            url: `${process.env.REACT_APP_BASE_URL}api/company-submission/downloads/${fileName}`,
            name: fileName,
          });
        }
      }
    } catch (error) {
      if (error.response?.status === 404) {
        // No existing data, allow form input
      } else {
        message.error("Failed to load company information");
      }
    }
  };
  useEffect(() => {
    fetchCompanyInfo();
  }, [userId]);

  const handleChange = (e) => {
    if (!isSaved) {
      const newValue = e.target.value;
      if (e.target.name === 'description') {
        if (newValue.length > 1000) {
          message.error('Company description cannot exceed 1000 characters');
          return;
        }
        setCharCount(newValue.length);
      }
      setCompanyInfo({ ...companyInfo, [e.target.name]: newValue });
      setHasUnsavedChanges(true);
    }
  };

  const handleFileChange = (info) => {
    if (!isSaved) {
      setFileList(info.fileList);
      setTotalFileList(info.fileList);
      setCountTotalFile(info.fileList.length);
    }
  };

  const handleAddFile = (file) => {
    if (countTotalFile >= 20) {
      message.error("Maximum 20 files allowed");
      return false;
    }

    const existingFilenames = totalFileList.map((f) => f.name);
    if (existingFilenames.includes(file.name)) {
      message.error(`The file "${file.name}" already exists. Please upload a file with a unique name.`);
      return false;
    }

    return true;
  };

  const handleRemoveFile = (file) => {
    if (file.uid.startsWith('existing-')) {
      setDeletedFileIds(prev => [...prev, file.fileId]);
    }
    setCompanyInfo(prev => ({
      ...prev,
      files: prev?.files?.filter(f => f.fileId !== file.fileId) || []
    }));
  };

  const validateDescription = () => {
    const lines = companyInfo.description.split('\n').length;
    
    if (companyInfo.description.length < 10) {
      message.error("Company description must have at least 50 characters");
      return false;
    }
    if (companyInfo.description.length > 1000) {
      message.error("Company description cannot exceed 1000 characters");
      return false;
    }
    if (lines > 20) {
      message.error("Company description cannot exceed 20 lines");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    
    if (!validateDescription()) {
      setIsLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("name", companyInfo.name);
    formData.append("website", companyInfo.website);
    formData.append("description", companyInfo.description);
    
    // Add all files from fileList to formData
    fileList.forEach((file) => {
      if (!file.uid.startsWith("existing")) {
        formData.append('files', file.originFileObj || file);
      }
    });

    // Add deleted file IDs to formData
    if (deletedFileIds.length > 0) {
      formData.append('deletedFileIds', JSON.stringify(deletedFileIds));
    }

    try {
      if (!companyInfo.name || !companyInfo.website || !companyInfo.description) {
        throw new Error("Please fill in all required fields");
      }

      if (fileList.length === 0 && !existingData) {
        throw new Error("Please upload at least one file");
      }

      const response = await axiosSecureInstance.post("/api/company-submission/submit", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 10000,
      });

      if (response.status === 201 || response.status === 200) {
        message.success(
          existingData
            ? "Company information updated successfully!"
            : "Company information saved successfully!"
        );
        setHasUnsavedChanges(false);
        const updatedData = response.data;
        setExistingData(updatedData);
        setCompanyInfo({
          name: updatedData.companyName || companyInfo.name,
          website: updatedData.websiteUrl || companyInfo.website,
          description: updatedData.companyInformation || companyInfo.description,
          files: updatedData.fileInfo?.filter((file) => file.generatedDoc !== true) || [],
        });
        setFileList([]);
        setTotalFileList([]);
        setCountTotalFile(0);
        setDeletedFileIds([]);
        setIsSaved(true);
        fetchCompanyInfo();
      }
    } catch (error) {
      if (error.response) {
        console.log("error", error);
        message.error(error.response.data.message || "Failed to save company information");
      } else if (error.request) {
        message.error("Network error: Unable to reach the server");
      } else {
        message.error(error.message);
      }
      throw error; // Re-throw the error to handle it in the modal
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateDoc = async () => {
    if (!companyInfo.website) {
      message.error("Please enter a website URL to generate the document");
      return;
    }

    setIsGeneratingDoc(true);
    try {
      const response = await axiosSecureInstance.post(
        "/api/company-submission/generate-docx",
        { url: companyInfo.website, userId },
        { responseType: "json" }
      );

      const { downloadUrl, fileName } = response.data;
      setDocPreview({ url: downloadUrl, name: fileName });
      message.success("DOCX file generated successfully!");
      const fileDetails = [
        {
          name: fileName,
          size: 0,
          url: downloadUrl,
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          parentId: null,
          isKnowledgeBaseShareable: false,
          isFileShared: false,
          sharedKnowledgeBaseOwner: null,
        },
      ];
      const knowledgeBaseResponse = await createKnowledgeBase({
        fileDetails,
        owner: userId,
      });
      if (knowledgeBaseResponse.success) {
        message.success("DOCX file added to Knowledge Base!");
      } else {
        message.error(`Failed to add file to Knowledge Base: ${knowledgeBaseResponse.message}`);
      }
    } catch (error) {
      console.error("Error in handleGenerateDoc:", error);
      if (error.response) {
        message.error(error.response.data.message || "Failed to generate DOCX file");
      } else if (error.request) {
        message.error("Network error: Unable to reach the server");
      } else {
        message.error(error.message);
      }
    } finally {
      setIsGeneratingDoc(false);
    }
  };

  const handleNext = () => {
    if (hasUnsavedChanges) {
      showUnsavedChangesModal();
    } else {
      navigate("/industry");
    }
  };

  const handleDeleteGeneratedFile = async () => {
    try {
      const fileId = docPreview.url.split('/').pop();
      const response = await axiosSecureInstance.delete(`/api/company-submission/generated-file/${fileId}`);
      
      if (response.status === 200) {
        message.success("Generated file deleted successfully");
        setDocPreview(null);
      }
    } catch (error) {
      console.error('Error deleting generated file:', error);
      message.error(error.response?.data?.message || "Failed to delete generated file");
    }
  };

  return (
    <div style={{ height: "100vh", overflow: "hidden" }}>
      <div className="company-info">
        <div className="company-info-logo">
          <img src={NewLogo} alt="Collab AI Logo" />
        </div>
        <h1 className="welcome-h1">Add Your Company Information</h1>
        <p className="company-info-subtitle">
          Provide details about your company to help us serve you better
        </p>

        <div className={`company-info-form ${isLoading ? "loading" : ""}`}>
          <Row gutter={[24, 24]}>
            <Col span={12}>
              <label className="form-label">
                Company Name
                <Tooltip title="Enter your official company name">
                  <InfoCircleOutlined />
                </Tooltip>
              </label>
              <Input
                name="name"
                value={companyInfo.name}
                onChange={handleChange}
                placeholder="Enter company name"
                disabled
              />
            </Col>
            <Col span={12}>
              <label className="form-label">
                Website URL
                <Tooltip title="Provide your company's website URL (e.g., https://example.com)">
                  <InfoCircleOutlined />
                </Tooltip>
              </label>
              <Input
                name="website"
                value={companyInfo.website}
                onChange={handleChange}
                placeholder="https://example.com"
                disabled={isSaved}
              />
            </Col>
            <Col span={24}>
              <label className="form-label">
                Company Description
                <Tooltip title="Describe your company's mission, services, and key information (50-500 words, max 20 lines)">
                  <InfoCircleOutlined />
                </Tooltip>
              </label>
              <TextArea
                name="description"
                value={companyInfo.description}
                onChange={handleChange}
                rows={4}
                maxRows={20}
                maxLength={1000}
                placeholder="Describe your company's mission and services (minimum 50 characters)"
                disabled={isSaved}
                showCount={{
                  formatter: ({ count }) => `${count}/1000 characters`,
                  max: 1000
                }}
              />
              <div style={{ marginTop: '8px', color: '#8c8c8c' }}>
                {charCount < 10 && (
                  <Text type="danger">Minimum 10 characters required</Text>
                )}
              </div>
            </Col>
            <Col span={12}>
              <label className="form-label">
                Brand Voice
                <Tooltip title="Upload your company information or brand document (PDF, DOCX, or TXT)">
                  <InfoCircleOutlined />
                </Tooltip>
              </label>
              {isSaved ? (
                <Upload
                  fileList={companyInfo.files?.map((file, index) => ({
                    uid: file.fileId || `existing-${index}`,
                    name: file.fileName,
                    status: 'done',
                    type: file.fileType
                  })) || []}
                  showUploadList={{
                    showRemoveIcon: false
                  }}
                  disabled={true}
                >
                  <Button icon={<UploadOutlined />} disabled>Upload Document</Button>
                </Upload>
              ) : (
                <Upload
                  onChange={handleFileChange}
                  beforeUpload={handleAddFile}
                  onRemove={handleRemoveFile}
                  accept=".pdf,.docx,.txt"
                  multiple={true}
                  fileList={fileList}
                >
                  <Button icon={<UploadOutlined />}>Upload Document</Button>
                </Upload>
              )}
            </Col>
          </Row>

          <div className="action-buttons-container">
            {!isSaved && (
              <Tooltip title="Save company information">
                <Button
                  type="primary"
                  loading={isLoading}
                  onClick={handleSubmit}
                  icon={<SaveOutlined />}
                >
                  Save
                </Button>
              </Tooltip>
            )}

            {isSaved && (
              <Tooltip title="Proceed to industry selection">
                <Button
                  type="primary"
                  onClick={handleNext}
                >
                  Next
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
        {isLoading && (
          <div className="loader-overlay">
            <Spin size="large" />
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyInfo;
